/**
 * 檔案用途區塊
 * @module InitThirdPartyProviderService
 * @purpose 讀取當前網址 query 參數中的三方登入必要欄位，初始化第三方登入提供者，並在欄位不足時導向指定頁面或首頁。
 * @external https://innotech.atlassian.net/browse/IN-46601
 * @external https://innotech.atlassian.net/browse/IN-47655
 * @external https://innotech.atlassian.net/browse/IN-54205
 * @external https://innotech.atlassian.net/browse/IN-55446
 * @external https://innotech.atlassian.net/browse/IN-55459
 * @external https://innotech.atlassian.net/browse/IN-113493
 * @external https://innotech.atlassian.net/browse/IN-132134
 */
import { PasskeyAction } from '@shared/enums';
import { Page } from '@shared/enums/route.enum';
import { ThirdPartyProvider } from '@shared/enums/third-party-provider.enum';
import { isAppKitRoute } from '@shared/helpers/appkit-enabled.helper';
import { useThirdPartyProvider } from '@shared/models';
import type { PasskeyThirdPartyPayload } from '@shared/models';
import { buildPasskeyEntryFailureRedirectUrl } from '@modules/passkey/helpers/passkey-callback.helper';
import {
  getProviderContract,
  parseCommonThirdPartyPayload,
  parsePasskeyActionParam,
  resolveProviderFromParam,
} from '@shared/third-party-providers/contracts';
import type { PasskeyCallbackReason } from '@modules/passkey/interfaces/passkey-callback.interface';
import { formatRelativePathname } from '@utilities/helpers/format.helper';
import React, { useEffect } from 'react';

/**
 * 宣告內容用途說明與單號關聯
 * @description 在 payload 不完整時導回 fallback 頁面。
 * @purpose 維持與既有第三方 SSO 相同的缺參數導頁行為。
 * @external https://innotech.atlassian.net/browse/IN-46601
 */
const redirectToFallback = (fallbackRedirectUrl: Nullable<string>) => {
  if (location.pathname === '/') {
    return;
  }

  location.href = fallbackRedirectUrl || '/';
};

/**
 * 宣告內容用途說明與單號關聯
 * @description Passkey 進入 URL 解析或驗證失敗時組結構化 callback。
 * @purpose 避免 bare redirect，讓 Fluid 能依 reason 顯示對應 UI。
 * @external https://innotech.atlassian.net/browse/IN-132161
 */
const redirectPasskeyEntryFailure = (
  redirectUrl: string,
  action: PasskeyAction,
  reason: PasskeyCallbackReason,
  state?: string,
) => {
  window.location.replace(buildPasskeyEntryFailureRedirectUrl(redirectUrl, action, reason, state));
};

/**
 * 宣告內容用途說明與單號關聯
 * @description 依 Passkey action 決定進入失敗的 callback reason。
 * @purpose 區分 forgot 缺 account 等情境。
 * @external https://innotech.atlassian.net/browse/IN-132161
 */
const resolvePasskeyEntryFailureReason = (payload: PasskeyThirdPartyPayload): PasskeyCallbackReason => {
  if (payload.action === PasskeyAction.ForgotPasswordVerify && !payload.account?.trim()) {
    return 'invalidPayload';
  }

  return 'invalidPayload';
};

/**
 * 宣告內容用途說明與單號關聯
 * @description 第三方登入初始化服務元件。
 * @purpose 在元件掛載後解析 URL 參數並呼叫第三方登入初始化流程。
 * @external https://innotech.atlassian.net/browse/IN-46601
 */
const InitThirdPartyProviderService = () => {
  /**
   * 宣告內容用途說明與單號關聯
   * @description 取得第三方登入初始化方法。
   * @purpose 使用共享模型提供的 hook 取得 init 函式供後續呼叫。
   * @external https://innotech.atlassian.net/browse/IN-46601
   */
  const { init } = useThirdPartyProvider();

  useEffect(() => {
    /**
     * 宣告內容用途說明與單號關聯
     * @description 以當前網址查詢字串建立 URLSearchParams 實例。
     * @purpose 統一透過瀏覽器 location.search 讀取登入初始化所需參數。
     * @external https://innotech.atlassian.net/browse/IN-55459
     */
    const searchParams = new URLSearchParams(location.search);
    /**
     * 宣告內容用途說明與單號關聯
     * @description 解析 clientId、redirectUrl、lang 等共通欄位。
     * @purpose 供各 provider contract 共用，避免重複解析 query。
     * @external https://innotech.atlassian.net/browse/IN-55459
     */
    const commonPayload = parseCommonThirdPartyPayload(searchParams);
    /**
     * 宣告內容用途說明與單號關聯
     * @description 將 provider 參數以不分大小寫方式映射為列舉值。
     * @purpose 避免大小寫差異造成 provider 無法識別，提升參數容錯性。
     * @external https://innotech.atlassian.net/browse/IN-113493
     */
    const matchedProvider = resolveProviderFromParam(searchParams.get('provider'));
    /**
     * 宣告內容用途說明與單號關聯
     * @description 依 provider 取得對應的解析與驗證契約。
     * @purpose 讓 Passkey 與 OAuth provider 使用不同必填規則。
     * @external https://innotech.atlassian.net/browse/IN-132161
     */
    const contract = getProviderContract(matchedProvider);
    const state = searchParams.get('state') ?? undefined;

    if (
      matchedProvider === ThirdPartyProvider.WalletConnect &&
      !isAppKitRoute(location.pathname) &&
      !searchParams.get('authCode') &&
      !searchParams.get('code')
    ) {
      window.location.replace(`${formatRelativePathname(Page.WalletConnect)}${location.search}${location.hash}`);
      return;
    }

    if (!contract) {
      redirectToFallback(commonPayload.redirectUrl);
      return;
    }

    /**
     * 宣告內容用途說明與單號關聯
     * @description 透過 contract 將 query 轉為 provider 專用 payload。
     * @purpose 集中 Passkey action / handoff 等欄位解析邏輯。
     * @external https://innotech.atlassian.net/browse/IN-132161
     */
    const payload = contract.parse({
      searchParams,
      pathname: location.pathname,
      commonPayload,
    });

    if (!payload) {
      if (matchedProvider === ThirdPartyProvider.Passkey && commonPayload.redirectUrl) {
        const action = parsePasskeyActionParam(searchParams.get('action'));
        if (action) {
          redirectPasskeyEntryFailure(commonPayload.redirectUrl, action, 'invalidPayload', state);
          return;
        }
      }

      redirectToFallback(commonPayload.redirectUrl);
      return;
    }

    /**
     * 宣告內容用途說明與單號關聯
     * @description 驗證 payload 是否具備進入 SSO 流程的必要欄位。
     * @purpose Passkey 與 OAuth 採不同 ready 條件（如 Passkey 不要求 clientId）。
     * @external https://innotech.atlassian.net/browse/IN-132161
     */
    const { ready, fallbackRedirectUrl } = contract.validate(payload);
    if (!ready) {
      if (payload.provider === ThirdPartyProvider.Passkey && fallbackRedirectUrl) {
        redirectPasskeyEntryFailure(
          fallbackRedirectUrl,
          payload.action,
          resolvePasskeyEntryFailureReason(payload),
          payload.state,
        );
        return;
      }

      redirectToFallback(fallbackRedirectUrl ?? commonPayload.redirectUrl);
      return;
    }

    /**
     * 宣告內容用途說明與單號關聯
     * @description 將驗證通過的 payload 寫入全域 store。
     * @purpose 供 Guard 與各 provider 頁面讀取初始化結果。
     * @external https://innotech.atlassian.net/browse/IN-113493
     */
    init(payload);
  }, [init]);

  return <></>;
};

export default InitThirdPartyProviderService;

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:23:08.486Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note Added and normalized comments into the required three-section layout, inserted declaration-level JSDoc blocks for mapped signatures using only provided origin tickets, formatted all @external entries as full Jira browse URLs, and kept runtime logic unchanged.
 */
